import type { AxiosError, AxiosInstance } from 'axios'
import {
  FALLBACK_API_BASE_URL,
  PRIMARY_API_BASE_URL,
  PRIMARY_TIMEOUT_MS,
  getActiveApiBaseUrl,
  hasFallbackHost,
  isFallbackActive,
  rememberFallbackHost,
} from './apiHost'

/** Обычный таймаут запроса — столько ждём от запасного хоста. */
const FALLBACK_TIMEOUT_MS = 30000

/** Метка на запросе: этот уже уходил на запасной хост, второй раз не пробуем. */
interface RetriableConfig {
  __apiFallbackRetry?: boolean
}

/**
 * Ручки, которые нельзя повторять вслепую: запрос мог дойти до сервера и
 * выполниться, а до нас не доехал только ответ. Повтор списал бы деньги или
 * прокрутил барабан второй раз.
 */
const UNSAFE_TO_REPEAT = ['/subscription', '/payment', '/spin']

const isIdempotent = (method?: string): boolean => {
  const m = (method ?? 'get').toLowerCase()
  return m === 'get' || m === 'head' || m === 'options'
}

const mayRepeat = (error: AxiosError): boolean => {
  if (isIdempotent(error.config?.method)) return true
  const url = error.config?.url ?? ''
  return !UNSAFE_TO_REPEAT.some(path => url.includes(path))
}

/**
 * Похоже ли на «хост недоступен», а не на осмысленный ответ бэка.
 * 4xx — это ответ, повторять его на другом сервере бессмысленно.
 */
const looksUnreachable = (error: AxiosError): boolean => {
  // Отменённый запрос (react-query снял его сам) сбоем не считается.
  if (error.code === 'ERR_CANCELED') return false
  if (!error.response) return true
  return error.response.status >= 500
}

/**
 * Учит инстанс axios ходить на запасной бэкенд, когда основной не отвечает.
 *
 * Вешать нужно до остальных перехватчиков инстанса: тогда неудачная попытка
 * успевает смениться удачной раньше, чем ошибка уедет в логи.
 */
export const withApiFallback = (instance: AxiosInstance): AxiosInstance => {
  if (!hasFallbackHost) return instance

  instance.interceptors.request.use(config => {
    const retry = config as typeof config & RetriableConfig

    if (retry.__apiFallbackRetry) {
      config.baseURL = FALLBACK_API_BASE_URL
      return config
    }

    config.baseURL = getActiveApiBaseUrl()

    // Пока сидим на основном хосте, первую попытку укорачиваем: за ней стоит
    // вторая, и суммарное ожидание не должно вырасти вдвое.
    if (!isFallbackActive()) {
      config.timeout = Math.min(config.timeout || PRIMARY_TIMEOUT_MS, PRIMARY_TIMEOUT_MS)
    }

    return config
  })

  instance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const config = error.config as
        | (NonNullable<AxiosError['config']> & RetriableConfig)
        | undefined

      if (
        !config ||
        config.__apiFallbackRetry ||
        isFallbackActive() ||
        !looksUnreachable(error) ||
        !mayRepeat(error)
      ) {
        return Promise.reject(error)
      }

      config.__apiFallbackRetry = true
      config.baseURL = FALLBACK_API_BASE_URL
      // Таймаут возвращаем обычный: запасной хост в Европе отвечает медленнее,
      // и укороченный лимит первой попытки здесь только мешал бы.
      config.timeout = FALLBACK_TIMEOUT_MS

      console.warn(
        `⚠️ ${PRIMARY_API_BASE_URL} не ответил, пробуем ${FALLBACK_API_BASE_URL}`
      )

      const response = await instance.request(config)
      // Дошло — значит основной хост недоступен именно у этого человека.
      rememberFallbackHost()
      return response
    }
  )

  return instance
}
